import { getImpersonationStateSnapshot } from '@revelio/auth';
import {
  Storage,
  StorageKey,
  StorageKind,
  StorageSubscribe,
} from '@revelio/core';

import type {
  PreferenceScope,
  UserPreferenceKey,
  UserPreferences,
} from '../types';
import { getDefaultPreferences } from './preference-defaults';
import { filterPreferencesForScope } from './preference-helpers';

// biome-ignore lint/complexity/noStaticOnlyClass: migrated from develop
export abstract class PreferenceBrowserStorage {
  private static storageKind = StorageKind.Session;

  // Kept apart per impersonated user, so their preferences never pick up your
  // session edits and yours are still there when impersonation stops.
  private static getStorageKey(scope: PreferenceScope): string {
    const { impersonatedUserId } = getImpersonationStateSnapshot();
    return impersonatedUserId
      ? `${StorageKey.UserPreferencesPrefix}${impersonatedUserId}-${scope}`
      : `${StorageKey.UserPreferencesPrefix}${scope}`;
  }

  public static get(scope: PreferenceScope): Partial<UserPreferences> {
    return Storage.readJson<Partial<UserPreferences>>(
      StorageKind.Session,
      // biome-ignore lint/complexity/noThisInStatic: migrated from develop
      this.getStorageKey(scope),
      {}
    );
  }

  public static update(
    scope: PreferenceScope,
    key: UserPreferenceKey,
    value: UserPreferences[UserPreferenceKey]
  ): UserPreferences {
    // biome-ignore lint/complexity/noThisInStatic: migrated from develop
    const current = this.get(scope);
    const merged = { ...current, [key]: value } as Partial<UserPreferences>;
    const filtered = filterPreferencesForScope(scope, merged);

    // biome-ignore lint/complexity/noThisInStatic: migrated from develop
    const storageKey = this.getStorageKey(scope);
    // biome-ignore lint/complexity/noThisInStatic: migrated from develop
    Storage.writeJson(this.storageKind, storageKey, filtered);
    // biome-ignore lint/complexity/noThisInStatic: migrated from develop
    StorageSubscribe.notify(this.storageKind, storageKey);
    return filtered as UserPreferences;
  }

  public static remove(scope: PreferenceScope, key: UserPreferenceKey): void {
    const storageKey = PreferenceBrowserStorage.getStorageKey(scope);
    const { [key]: _removed, ...rest } = PreferenceBrowserStorage.get(scope);
    Storage.writeJson(PreferenceBrowserStorage.storageKind, storageKey, rest);
    StorageSubscribe.notify(PreferenceBrowserStorage.storageKind, storageKey);
  }

  public static reset(scope: PreferenceScope): void {
    // biome-ignore lint/complexity/noThisInStatic: migrated from develop
    const storageKey = this.getStorageKey(scope);
    const defaults = filterPreferencesForScope(
      scope,
      getDefaultPreferences(scope)
    );
    // biome-ignore lint/complexity/noThisInStatic: migrated from develop
    Storage.writeJson(this.storageKind, storageKey, defaults);
    // biome-ignore lint/complexity/noThisInStatic: migrated from develop
    StorageSubscribe.notify(this.storageKind, storageKey);
  }

  public static set(
    scope: PreferenceScope,
    preferences: UserPreferences
  ): void {
    // biome-ignore lint/complexity/noThisInStatic: migrated from develop
    const storageKey = this.getStorageKey(scope);
    const filtered = filterPreferencesForScope(scope, preferences);
    // biome-ignore lint/complexity/noThisInStatic: migrated from develop
    Storage.writeJson(this.storageKind, storageKey, filtered);
    // biome-ignore lint/complexity/noThisInStatic: migrated from develop
    StorageSubscribe.notify(this.storageKind, storageKey);
  }

  public static listenForChanges(
    scope: PreferenceScope,
    callback: (preferences: UserPreferences) => void
  ) {
    // biome-ignore lint/complexity/noThisInStatic: migrated from develop
    const storageKey = this.getStorageKey(scope);
    // biome-ignore lint/complexity/noThisInStatic: migrated from develop
    return StorageSubscribe.subscribe(this.storageKind, storageKey, () => {
      const prefs = Storage.readJson<UserPreferences>(
        // biome-ignore lint/complexity/noThisInStatic: migrated from develop
        this.storageKind,
        storageKey,
        getDefaultPreferences(scope)
      );
      callback(filterPreferencesForScope(scope, prefs) as UserPreferences);
    });
  }
}
